<script setup>
import {defineProps, watch} from "vue";
import {onMounted} from "vue";
import {usePortalContentAndDesignStore} from "../../stores/partners/portalContentAndDesignStore";
import {hexToRgbString, injectStyleBlock, isCustomColor, isDarkColor} from "../../helpers/common";
// Shared with build/fonts.js and config/fonts.php, so the ids here always match the
// self-hosted stylesheets generated into public/fonts.
import brandFonts from "../../../fonts/families.json";

    const BRAND_FONT_LINK_ID = 'brand-font-stylesheet';

    const props = defineProps({
        data: {
            type: Object,
            required: false
        },
        authView: {
            type: Boolean,
            default: false
        },
        isPreview: {
            type: Boolean,
            default: false
        },
        socialRegisterErrors: {
            type: [Object, Array],
            default: null
        },
        socialLoginErrors: {
            type: [Object, Array],
            default: null
        },
        prefillEmail: {
            type: String,
            default: null
        },
        autoSubmit: {
            type: Boolean,
            default: false
        },
    });

    const store = usePortalContentAndDesignStore()

    onMounted(() => {

        store.initializePortalContent(props.data, props.authView, props.isPreview);
        if (props.socialLoginErrors) {
            store.loginFormErrors.validationErrors = props.socialLoginErrors;
        }
        if (props.socialRegisterErrors) {
            store.signupFormErrors.validationErrors = props.socialRegisterErrors;
        }
        // Prefill and lock the login email when passed via URL (embedded dashboard).
        if (props.prefillEmail) {
            store.loginFormData.email = props.prefillEmail;
            store.loginEmailLocked = true;
            // Optionally auto-submit the first login step (handled by the login form).
            store.loginAutoSubmit = props.autoSubmit;
        }
        store.portalInitialized = true
    });

    // Apply the brand font family live. This lives in the always-mounted initializer
    // (rather than only in the Live Designer's BrandSettings panel) so applying a design
    // theme from the Themes tab updates the font immediately, without opening the Layout tab.
    const applyBrandFont = (fontFamily) => {
        const font = brandFonts.fonts[fontFamily] || brandFonts.fonts[brandFonts.default];

        document.documentElement.style.fontFamily = `"${font.family}", sans-serif`;

        const existingLink = document.getElementById(BRAND_FONT_LINK_ID);
        // Carry over the cache-busting token from the server-rendered tag so a swapped
        // stylesheet is versioned the same way as the one the page loaded with.
        const version = existingLink
            ? new URL(existingLink.href, window.location.origin).search
            : '';
        const href = `/fonts/${font.id}/index.css${version}`;

        if (existingLink) {
            existingLink.setAttribute('href', href);
        } else {
            const link = document.createElement('link');
            link.id = BRAND_FONT_LINK_ID;
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
        }
    };

    watch(() => store.portalLayoutSettings?.portal_layout_brand_font_family, (newFont) => {
        applyBrandFont(newFont);
    });

    watch(() => store.portalLayoutSettings, (newSettings, oldSettings) => {
        if(newSettings) {
            // Clear the applied-theme badge once the user manually diverges from the theme.
            store.syncAppliedDesignTemplate();

            let dynamicCss = ''

            if (isCustomColor(newSettings.public_page_background_color)) {
                dynamicCss += `
                    .bg-custom-bg {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_background_color)});
                    }
                `;
            }

            // Two different classes for the same class. Check if this is intentional. (copied from pub/affiliate.blade.php and auth/affiliate.blade.php)
            if (isCustomColor(newSettings.portal_layout_background_color)) {
                dynamicCss += `
                    .bg-custom-bg {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_background_color) }, var(--tw-bg-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_background_gradient_color)) {
                dynamicCss += `
                    .from-custom-gradient-bg {
                        background: linear-gradient(rgba(${hexToRgbString(newSettings.public_page_background_gradient_color)}), transparent);
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_button_color)) {
                dynamicCss += `
                    .bg-custom-content-btn {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_content_button_color)});
                        border-color: rgba(${hexToRgbString(newSettings.public_page_content_button_color)});
                        color: #ffffff;
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_text_color)) {
                dynamicCss += `
                    .text-custom-text {
                        color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-text-opacity));
                    }
                    .bg-custom-text {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-bg-opacity));
                    }
                    .border-custom-text {
                        border-color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-border-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_text_color)) {
                dynamicCss += `
                    .text-custom-text {
                        color: rgba(${hexToRgbString(newSettings.portal_layout_text_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_link_color)) {
                dynamicCss += `
                    .text-custom-link {
                        color: rgba(${hexToRgbString(newSettings.public_page_link_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_brand_primary_color)) {
                dynamicCss += `
                    .bg-brand-bg {
                        background-color: rgba(${hexToRgbString(newSettings.portal_layout_brand_primary_color)});
                    }
                    .bg-custom-primary {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-bg-opacity));
                    }
                    .text-custom-primary {
                            color: ${ newSettings.portal_layout_brand_primary_color };
                        }

                    .border-custom-primary {
                            border-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-border-opacity));
                        }

                    .hover\:text-custom-primary:hover {
                            color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-text-opacity));
                        }

                    .hover\:bg-custom-primary:hover {
                            background-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-bg-opacity));
                        }

                    .hover\:border-custom-primary:hover {
                            border-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-border-opacity));
                        }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_background_color)) {
                dynamicCss += `
                    .bg-content-bg {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_content_background_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_text_color)) {
                dynamicCss += `
                    .text-custom-content-text {
                        color: rgba(${hexToRgbString(newSettings.public_page_content_text_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_link_color)) {
                dynamicCss += `
                    .text-custom-content-text a:not(.portal-btn-text) {
                        color: rgba(${hexToRgbString(newSettings.public_page_content_link_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_button_color)) {
                dynamicCss += `
                    .bg-custom-btn {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_button_color) });
                        border-color: rgba(${ hexToRgbString(newSettings.portal_layout_button_color) });
                        color: #ffffff;
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_link_color)) {
                dynamicCss += `
                    .text-custom-link {
                        color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                    .group:hover .group-hover\:text-custom-link {
                        --tw-text-opacity: 1;
                        color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                    .border-custom-border {
                        border-color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            // Referral Portal Specific Colors
            if (isCustomColor(newSettings.referral_portal_background_color)) {
                dynamicCss += `
                    .bg-custom-bg {
                        background-color: rgba(${ hexToRgbString(newSettings.referral_portal_background_color) }, var(--tw-bg-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_text_color)) {
                dynamicCss += `
                    .text-custom-text {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_text_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_link_color)) {
                dynamicCss += `
                    .text-custom-link {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_link_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_background_color)) {
                dynamicCss += `
                    .bg-content-custom-bg {
                        background-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_background_color) }, var(--tw-bg-opacity));
                    }
                    .content-custom-bg {
                        background-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_background_color) }, var(--tw-bg-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_text_color)) {
                dynamicCss += `
                    .text-content-custom-text {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_text_color) }, var(--tw-text-opacity));
                    }
                    .content-custom-text {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_text_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_text_color_secondary)) {
                dynamicCss += `
                    .text-content-custom-text-secondary {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_text_color_secondary) }, var(--tw-text-opacity));
                    }
                    .content-custom-text-secondary {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_text_color_secondary) }, var(--tw-text-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_link_color)) {
                dynamicCss += `
                    .text-content-custom-link {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_link_color) }, var(--tw-text-opacity));
                    }
                    .content-custom-link {
                        color: rgba(${ hexToRgbString(newSettings.referral_portal_content_link_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_button_color)) {
                dynamicCss += `
                    .bg-custom-content-btn {
                        background-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_button_color) });
                        border-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_button_color) });
                        color: #ffffff;
                    }
                `;
            }

            if (isCustomColor(newSettings.referral_portal_content_border_color)) {
                dynamicCss += `
                    .border-content-custom-color {
                        border-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_border_color) }, 1) !important;
                    }
                    .content-custom-color {
                        border-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_border_color) }, 1) !important;
                    }
                    .divide-content-custom-color > * + * {
                        border-top-color: rgba(${ hexToRgbString(newSettings.referral_portal_content_border_color) }, 1) !important;
                    }
                `;
            }

            // Dark dashboard contrast fixes. Many dashboard surfaces use subtle
            // `bg-black bg-opacity-5` containers and default (light) inputs that vanish
            // or clash on a dark background. Scoped to the dashboard content only
            // (.portal-content-dark on <main>) so the Live Designer panel is untouched.
            if (isDarkColor(newSettings.portal_layout_background_color)) {
                // Elevated surface for floating panels (dropdowns) so they match the theme
                // (near-black for Onyx, navy for Midnight) instead of a hardcoded slate.
                const elevatedSurface = store.getPortalLayoutElevatedSurface;

                dynamicCss += `
                    .portal-content-dark .bg-black.bg-opacity-5,
                    .portal-content-dark .bg-black.bg-opacity-10,
                    .portal-content-dark .bg-gray-50,
                    .portal-content-dark .bg-gray-100 {
                        background-color: rgba(255, 255, 255, 0.06);
                    }
                    /* Plain white content cards -> subtle dark panels. Excludes
                       opacity-modified whites (small chips/overlays) and buttons so their
                       intended styling is preserved. */
                    .portal-content-dark .bg-white:not([class*="bg-opacity"]):not(button):not(a) {
                        background-color: rgba(255, 255, 255, 0.05);
                    }
                    .portal-content-dark .bg-gray-200 {
                        background-color: rgba(255, 255, 255, 0.14);
                    }
                    .portal-content-dark .hover\\:bg-gray-50:hover,
                    .portal-content-dark .hover\\:bg-gray-100:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }
                    .portal-content-dark .border-gray-100,
                    .portal-content-dark .border-gray-200,
                    .portal-content-dark .border-gray-300 {
                        border-color: rgba(255, 255, 255, 0.14);
                    }
                    .portal-content-dark .text-gray-900,
                    .portal-content-dark .text-gray-800 {
                        color: rgba(255, 255, 255, 0.92);
                    }
                    .portal-content-dark .text-gray-700,
                    .portal-content-dark .text-gray-600 {
                        color: rgba(255, 255, 255, 0.72);
                    }
                    .portal-content-dark .text-gray-500,
                    .portal-content-dark .text-gray-400 {
                        color: rgba(255, 255, 255, 0.55);
                    }
                    .portal-content-dark input:not([type=checkbox]):not([type=radio]),
                    .portal-content-dark textarea,
                    .portal-content-dark select,
                    .portal-content-dark .portal-daterange-trigger {
                        background-color: rgba(255, 255, 255, 0.05);
                        border-color: rgba(255, 255, 255, 0.18);
                        color: inherit;
                    }
                    .portal-content-dark input::placeholder,
                    .portal-content-dark textarea::placeholder {
                        color: rgba(255, 255, 255, 0.4);
                    }

                    /* Dropdown/options panels float over the dark page, so a translucent
                       white surface reads as washed-out. Give them a solid dark panel
                       (matching the multiselect/date dropdowns) with a legible border. */
                    .portal-content-dark .portal-dropdown-panel.bg-white:not([class*="bg-opacity"]) {
                        background-color: ${elevatedSurface};
                        border-color: rgba(255, 255, 255, 0.14);
                        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
                    }

                    /* Modals always stay light (white surface, dark text). The modal
                       renders inline inside .portal-content-dark, so re-assert the
                       default light utilities within the modal panel. Higher specificity
                       (extra .portal-force-light) beats the dark overrides above. */
                    .portal-content-dark .portal-force-light.bg-white:not([class*="bg-opacity"]),
                    .portal-content-dark .portal-force-light .bg-white:not([class*="bg-opacity"]) {
                        background-color: #ffffff;
                    }
                    .portal-content-dark .portal-force-light .bg-gray-50 {
                        background-color: #f9fafb;
                    }
                    .portal-content-dark .portal-force-light .bg-gray-100 {
                        background-color: #f3f4f6;
                    }
                    .portal-content-dark .portal-force-light .bg-gray-200 {
                        background-color: #e5e7eb;
                    }
                    .portal-content-dark .portal-force-light .bg-black.bg-opacity-5 {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    .portal-content-dark .portal-force-light .bg-black.bg-opacity-10 {
                        background-color: rgba(0, 0, 0, 0.1);
                    }
                    .portal-content-dark .portal-force-light .border-gray-100 {
                        border-color: #f3f4f6;
                    }
                    .portal-content-dark .portal-force-light .border-gray-200 {
                        border-color: #e5e7eb;
                    }
                    .portal-content-dark .portal-force-light .border-gray-300 {
                        border-color: #d1d5db;
                    }
                    .portal-content-dark .portal-force-light .text-gray-900,
                    .portal-content-dark .portal-force-light .text-gray-800 {
                        color: #1f2937;
                    }
                    .portal-content-dark .portal-force-light .text-gray-700 {
                        color: #374151;
                    }
                    .portal-content-dark .portal-force-light .text-gray-600 {
                        color: #4b5563;
                    }
                    .portal-content-dark .portal-force-light .text-gray-500 {
                        color: #6b7280;
                    }
                    .portal-content-dark .portal-force-light .text-gray-400 {
                        color: #9ca3af;
                    }
                    .portal-content-dark .portal-force-light input:not([type=checkbox]):not([type=radio]),
                    .portal-content-dark .portal-force-light textarea,
                    .portal-content-dark .portal-force-light select {
                        background-color: #ffffff;
                        border-color: #d1d5db;
                        color: #111827;
                    }
                    .portal-content-dark .portal-force-light input::placeholder,
                    .portal-content-dark .portal-force-light textarea::placeholder {
                        color: #9ca3af;
                    }
                `;
            }

            // Optional grainy background texture (opt-in via theme/setting).
            if (newSettings.portal_layout_background_texture === 'grain') {
                const noise = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";
                dynamicCss += `
                    body::after {
                        content: '';
                        position: fixed;
                        inset: 0;
                        pointer-events: none;
                        z-index: 9999;
                        opacity: 0.06;
                        background-image: url("${noise}");
                        background-size: 180px 180px;
                    }
                `;
            }

            injectStyleBlock(dynamicCss, 'custom-style-colors');
        }
    }, { deep: true });
</script>
<template>

</template>
